import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { CirclePlus, RefreshCw } from "lucide-react";
import { usePutBranchMutation } from "@/services/backend/company/api/branch";
import { useState } from "react";
import { v4 } from "uuid";
import { Label } from "@/shared/components/ui/label";
import t from "@/lang/lang";
import { CompanySelect } from "@/services/backend/company/components/company-select";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { TextInput } from "@/shared/components/form/text-input";
import { Address } from "@/services/backend/addresses/types/address";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";

interface Props {
  onCreated: (branchId: string) => void;
}

export function CreateBranchDialog({ onCreated }: Props) {
  const [open, setOpen] = useState(false);
  const [create, { isLoading, error, isSuccess, reset }] =
    usePutBranchMutation();
  const [request, setRequest] = useState({
    id: v4(),
    name: "",
    companyId: "",
    address: {} as Address,
    phone: "",
    email: "",
    branchManager: "",
  });

  const doCreate = () => {
    if (isLoading) {
      return;
    }

    create(request);
  };

  if (isSuccess) {
    setOpen(false);
    reset();
    onCreated(request.id);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <CirclePlus />
          <span>Niederlassung</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Niederlassung erstellen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Es muss ein Name und die Zugehörigkeit zum Unternehmen festgelegt werden. Eine Niederlassung kann nur zu einem Unternehmen gehören (auch wenn sie an der gleichen Adresse ist). Die Zugehörigkeit kann nicht mehr geändert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <TextInput
          label={t("Name der Niederlassung")}
          field="name"
          text={request.name}
          onTextChange={(name) => setRequest({ ...request, name })}
          errors={inputValidationErrorMessages(error, "name")}
        />
        <div className="grid gap-1.5">
          <Label>{t("Unternehmen")}</Label>
          <CompanySelect
            companyId={request.companyId}
            onCompanyIdChange={(companyId) =>
              setRequest({ ...request, companyId })
            }
          />
          <InputValidationErrors field="companyId" error={error} />
        </div>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doCreate} disabled={isLoading}>
            {isLoading && <RefreshCw className="mr-2 animate-spin" />}
            {t("Erstellen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
